import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Input,
  FormLabel,
  Textarea,
  Flex,
  useTheme,
} from "@chakra-ui/react";
import SubmitButton from "../../buttons/SubmitButton";
import { useDispatch } from "react-redux";
import { createCourses, updateCourse } from "../../../store/slice/courseSlice";
import { useToastContext } from "../../toastNotificationProvider/ToastNotificationProvider";

const AddCourseModal = ({ isOpen, onClose, editData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { addNotification } = useToastContext();
  const { primaryBlueLight, primaryBlue } = theme.colors.pallete;

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    displayOrder: "",
  });

  const [errors, setErrors] = useState({});

  // Populate fields when editData is available
  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setFormData((prev) => {
          if (
            prev.title !== editData.title ||
            prev.description !== editData.description ||
            prev.displayOrder !== editData.displayOrder
          ) {
            return {
              title: editData.title || "",
              description: editData.description || "",
              displayOrder: editData.displayOrder || "",
            };
          }
          return prev;
        });
      } else {
        setFormData({ title: "", description: "", displayOrder: "" });
      }
    }
  }, [isOpen, editData]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({ title: "", description: "", displayOrder: "" });
    }
    setErrors({});
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.title) newErrors.title = "Please enter course name";
    if (!formData.description)
      newErrors.description = "Please enter description";

    const displayOrder = formData.displayOrder?.toString().trim();

    if (!displayOrder) newErrors.displayOrder = "Please enter display order";
    else if (isNaN(displayOrder) || Number(displayOrder) <= 0)
      newErrors.displayOrder = "Display order should be a number";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      title: formData.title,
      description: formData.description,
      displayOrder: Number(formData.displayOrder),
    };

    setIsSubmitLoading(true);

    if (editData) {
      dispatch(updateCourse({ id: editData.id, formData }))
        .unwrap()
        .then((response) => {
          addNotification("Course updated successfully", "success", 3000);
          onClose();
        })
        .catch((error) => {
          addNotification(error, "warning", 3000);
        })
        .finally(() => {
          setIsSubmitLoading(false);
        });
    } else {
      // Call createCourses API for new course
      dispatch(createCourses(payload))
        .unwrap()
        .then((response) => {
          addNotification("Course created successfully", "success", 3000);
          onClose();
        })
        .catch((error) => {
          addNotification(error, "warning", 2000);
        })
        .finally(() => {
          setIsSubmitLoading(false);
        });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={"18px"} fontWeight={400} textAlign="center">
          {editData ? "Update Course" : "Add Course"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Course Name */}
          <FormControl isRequired isInvalid={errors.title}>
            <FormLabel>Course Name</FormLabel>
            <Input
              name="title"
              fontSize={"14px"}
              borderColor={"gray.300"}
              placeholder="Enter course name"
              value={formData.title}
              onChange={handleChange}
            />
            {errors.title && (
              <p style={{ color: "red", fontSize: "14px" }}>{errors.title}</p>
            )}
          </FormControl>

          {/* Description */}
          <FormControl mt={4} isRequired isInvalid={errors.description}>
            <FormLabel>Description</FormLabel>
            <Textarea
              name="description"
              borderColor={"gray.300"}
              fontSize={"14px"}
              placeholder="Enter description"
              value={formData.description}
              onChange={handleChange}
            />
            {errors.description && (
              <p style={{ color: "red", fontSize: "14px" }}>
                {errors.description}
              </p>
            )}
          </FormControl>

          {/* Display Order */}
          <FormControl mt={4} isRequired isInvalid={errors.displayOrder}>
            <FormLabel>Display Order</FormLabel>
            <Input
              name="displayOrder"
              fontSize={"14px"}
              borderColor={"gray.300"}
              placeholder="Enter display order"
              value={formData.displayOrder}
              onChange={handleChange}
            />
            {errors.displayOrder && (
              <p style={{ color: "red", fontSize: "14px" }}>
                {errors.displayOrder}
              </p>
            )}
          </FormControl>
        </ModalBody>

        {/* Submit Button */}
        <ModalFooter>
          <Flex w={"full"} justifyContent={"center"}>
            <SubmitButton
              text={editData ? "Update" : "Submit"}
              backColor={primaryBlue}
              textColor="white"
              hoverColor={primaryBlueLight}
              onClickHandler={handleSubmit}
              isLoading={isSubmitLoading}
            />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddCourseModal;
